// ANGEL FLIGHT — ENDLESS "Last Stand" mode (a distinct, replayable game mode).
//
// Where the campaign missions are FIXED, hand-authored survive-timer sorties,
// Last Stand is an ENDLESS escalating horde: waves never stop and steadily grow
// in size + threat mix, you hold until the bird goes down, and the run is scored
// on kills / streak / survival (a persisted best per mode). It reuses the whole
// sim — EnemySystem (wave director), FiringSystem, EconomySystem — verbatim;
// only the objective semantics differ (objective: 'endless' → ObjectiveSystem
// never timer-clears, grading skips the wipe-cap, GameLoop banks the best on the
// downed run). It is NOT part of the campaign (missionIndexOf returns -1) so it
// never touches mission progression.
//
// NO friendlies: Last Stand is a pure horde score-chase, so the ONLY way a run
// ends is the hull going down (no friendly-fire fail). Pure data + a pure
// generator (no rng here — the SIM rng still varies lanes/formations per run).

import type { EnemyId, MissionDef, WaveDef } from '../core/types';

export const ENDLESS_ID = 'endless-laststand';

/** How many escalating waves to author. Far more than any run survives — the
 *  ramp is what matters, not the ceiling (a strong run ends well before this). */
const WAVE_COUNT = 140;

/**
 * Build the escalating wave list. Each wave grows the filler horde and folds in
 * tougher archetypes as the index climbs, while the spacing tightens — so the
 * pressure ramps on two axes (more enemies, arriving faster). Deterministic +
 * pure (same list every boot); per-run variety comes from the sim rng driving
 * spawn lanes + formations. EnemySystem clamps concurrent live count to
 * BALANCE.enemies.maxLive and trickles each wave in, so big late counts read as
 * sustained pressure rather than an instant wall.
 */
export function buildEndlessWaves(): WaveDef[] {
  const waves: WaveDef[] = [];
  let t = 2_000;
  for (let i = 0; i < WAVE_COUNT; i++) {
    const spawns: Partial<Record<EnemyId, number>> = {};
    // Filler horde — always present, scales steadily.
    spawns.shambler = 3 + Math.floor(i * 0.7);
    spawns.runner = 1 + Math.floor(i * 0.5);
    // Street riflemen (chip pressure) from wave 4.
    if (i >= 3) spawns.marauder = 1 + Math.floor((i - 3) * 0.25);
    // Brutes (tanky walls) from wave 7, slowly more.
    if (i >= 6) spawns.brute = 1 + Math.floor((i - 6) * 0.12);
    // Acid spitters on the even mid waves.
    if (i >= 5 && i % 2 === 0) spawns.spitter = 1 + Math.floor((i - 5) * 0.15);
    // RPG screamers on the odd late waves — the priority kill (flare them).
    if (i >= 8 && i % 2 === 1) spawns.screamer = 1 + Math.floor((i - 8) * 0.12);

    // Milestone callouts so the escalation reads on the HUD.
    let banner: string | undefined;
    if (i === 0) banner = 'LAST STAND — HOLD';
    else if (i % 10 === 9) banner = `WAVE ${i + 1} — THE SURGE`;
    else if (i % 5 === 4) banner = `WAVE ${i + 1}`;

    waves.push({ atMs: t, spawns, banner });

    // Spacing tightens from 9s toward a 4.2s floor over the first ~30 waves.
    t += Math.max(4_200, 9_000 - i * 160);
  }
  return waves;
}

const ENDLESS_WAVES = buildEndlessWaves();

export const endless: MissionDef = {
  id: ENDLESS_ID,
  name: 'Last Stand',
  biome: 'Endless Horde — Dusk',
  objective: 'endless',
  theme: 'dusk-suburb',
  // Not used to clear (ObjectiveSystem 'endless' never timer-wins) — kept a valid
  // positive number (the authored ramp's full length) for any generic reader.
  durationMs: (ENDLESS_WAVES[ENDLESS_WAVES.length - 1]?.atMs ?? 0) + 60_000,
  brief:
    'No exfil. No timer. The horde just keeps coming and it only gets worse — hold the line and rack the count. The bird goes down when it goes down. How long can you last?',
  waves: ENDLESS_WAVES,
};
